import React, { useState, useEffect } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Image, Alert } from 'react-native';
import { useUser } from '../Widgets/UserContext';

const SERVER_URL = process.env.NODE_ENV === 'production' 
  ? 'https://us-central1-my-tassel.cloudfunctions.net/api'
  : 'https://us-central1-my-tassel.cloudfunctions.net/api';

const Unsubscribe = ({ route }) => {
  const [email, setEmail] = useState('');
  const [graduateEmail, setGraduateEmail] = useState('');
  const [reason, setReason] = useState('');
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');
  const { currentUser } = useUser();

  useEffect(() => {
    // Parse URL parameters for pre-filled email
    if (route?.params?.email) {
      setEmail(decodeURIComponent(route.params.email));
    } else if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const urlEmail = urlParams.get('email');
      const urlGraduate = urlParams.get('graduate');
      
      if (urlEmail) {
        setEmail(decodeURIComponent(urlEmail));
      }
      
      if (urlGraduate) {
        setGraduateEmail(decodeURIComponent(urlGraduate));
      }
    }

    // Auto-fill with current user email if logged in and no email in URL
    if (!email && currentUser?.email) {
      setEmail(currentUser.email);
    }
  }, [route, currentUser]);

  const handleUnsubscribe = async () => {
    if (!email.trim()) {
      setError('Please enter your email address');
      return;
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address');
      return;
    }

    setIsUnsubscribing(true);
    setError('');

    try {
      const response = await fetch(`${SERVER_URL}/unsubscribe-from-reminders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          reason: reason.trim() || 'User requested via website',
          graduateEmail: graduateEmail.trim() || null
        }),
      });

      const result = await response.json();

      if (result.success) {
        setShowSuccess(true);
        setEmail('');
        setReason('');
        setGraduateEmail('');
      } else {
        setError(result.error || 'Failed to unsubscribe. Please try again.');
      }
    } catch (error) {
      console.error('Unsubscribe error:', error);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsUnsubscribing(false);
    }
  };

  const resetForm = () => {
    setShowSuccess(false);
    setError('');
    setEmail(currentUser?.email || '');
    setReason('');
    setGraduateEmail('');
  };

  if (showSuccess) {
    return (
      <View style={styles.container}>
        <View style={styles.card}>
          <View style={styles.logoContainer}>
            <Image 
              source={{ uri: 'https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0' }}
              style={styles.logo}
              resizeMode="contain"
            />
          </View>
          
          <View style={styles.successContainer}>
            <Text style={styles.successIcon}>✅</Text>
            <Text style={styles.successTitle}>Successfully Unsubscribed</Text>
            <Text style={styles.successMessage}>
              You have been unsubscribed from reminder emails. You will no longer receive 
              reminder notifications about upcoming graduation events.
            </Text>
            
            <Text style={styles.noteText}>
              <Text style={styles.boldText}>Note:</Text> If you RSVP to any events in the future, 
              you will automatically be resubscribed to receive reminder emails for those events.
            </Text>

            <TouchableOpacity 
              style={styles.secondaryButton}
              onPress={resetForm}
            >
              <Text style={styles.secondaryButtonText}>Unsubscribe Another Email</Text>
            </TouchableOpacity>

            <TouchableOpacity 
              style={styles.linkButton}
              onPress={() => {
                if (typeof window !== 'undefined') {
                  window.location.href = 'https://mytassel.com';
                }
              }}
            >
              <Text style={styles.linkButtonText}>Return to MyTassel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <View style={styles.logoContainer}>
          <Image 
            source={{ uri: 'https://firebasestorage.googleapis.com/v0/b/my-tassel.appspot.com/o/emailAssets%2Flogo.png?alt=media&token=8d69cd10-5bc6-4344-b266-b04dac2c80f0' }}
            style={styles.logo}
            resizeMode="contain"
          />
        </View>

        <View style={styles.formContainer}>
          <Text style={styles.title}>Unsubscribe from Reminder Emails</Text>
          
          <Text style={styles.description}>
            We're sorry to see you go! Use this form to unsubscribe from reminder emails 
            about graduation events. You'll still be able to view graduation pages and 
            registries when shared with you.
          </Text>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Email Address</Text>
            <TextInput
              style={styles.input}
              value={email}
              onChangeText={setEmail}
              placeholder="Enter your email address"
              placeholderTextColor="#999"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
            />
          </View>

          <View style={styles.inputContainer}>
            <Text style={styles.label}>Reason (Optional)</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={reason}
              onChangeText={setReason}
              placeholder="Let us know why you're unsubscribing (optional)"
              placeholderTextColor="#999"
              multiline
              numberOfLines={3}
            />
          </View>

          {error ? (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>❌ {error}</Text>
            </View>
          ) : null}

          <TouchableOpacity 
            style={[styles.unsubscribeButton, isUnsubscribing && styles.buttonDisabled]}
            onPress={handleUnsubscribe}
            disabled={isUnsubscribing}
          >
            <Text style={styles.unsubscribeButtonText}>
              {isUnsubscribing ? 'Processing...' : 'Unsubscribe from Reminders'}
            </Text>
          </TouchableOpacity>

          <View style={styles.footerContainer}>
            <Text style={styles.footerText}>
              <Text style={styles.boldText}>Note:</Text> This only affects reminder emails. 
              If you RSVP to events in the future, you'll automatically receive reminders for those events.
            </Text>

            <TouchableOpacity 
              style={styles.linkButton}
              onPress={() => {
                if (typeof window !== 'undefined') {
                  window.location.href = 'https://mytassel.com';
                }
              }}
            >
              <Text style={styles.linkButtonText}>← Return to MyTassel</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 12,
    padding: 30,
    maxWidth: 500,
    width: '100%',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 5,
  },
  logoContainer: {
    alignItems: 'center',
    marginBottom: 30,
  },
  logo: {
    width: 80,
    height: 80,
  },
  formContainer: {
    width: '100%',
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#1a3a2a',
    textAlign: 'center',
    marginBottom: 15,
  },
  description: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 30,
  },
  inputContainer: {
    marginBottom: 20,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    backgroundColor: '#fafafa',
  },
  textArea: {
    height: 80,
    textAlignVertical: 'top',
  },
  errorContainer: {
    backgroundColor: '#ffebee',
    borderRadius: 8,
    padding: 12,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#ffcdd2',
  },
  errorText: {
    color: '#c62828',
    fontSize: 14,
    fontWeight: '500',
  },
  unsubscribeButton: {
    backgroundColor: '#dc3545',
    borderRadius: 8,
    padding: 15,
    alignItems: 'center',
    marginBottom: 20,
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
  },
  unsubscribeButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: 'bold',
  },
  secondaryButton: {
    backgroundColor: '#6c757d',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    marginBottom: 15,
  },
  secondaryButtonText: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
  },
  linkButton: {
    padding: 10,
    alignItems: 'center',
  },
  linkButtonText: {
    color: '#4c956c',
    fontSize: 14,
    fontWeight: '600',
  },
  footerContainer: {
    marginTop: 20,
    paddingTop: 20,
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  footerText: {
    fontSize: 12,
    color: '#888',
    textAlign: 'center',
    lineHeight: 16,
    marginBottom: 15,
  },
  boldText: {
    fontWeight: 'bold',
  },
  successContainer: {
    alignItems: 'center',
    width: '100%',
  },
  successIcon: {
    fontSize: 48,
    marginBottom: 20,
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#4c956c',
    textAlign: 'center',
    marginBottom: 15,
  },
  successMessage: {
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
    lineHeight: 22,
    marginBottom: 20,
  },
  noteText: {
    fontSize: 14,
    color: '#888',
    textAlign: 'center',
    lineHeight: 18,
    marginBottom: 30,
    paddingHorizontal: 20,
  },
});

export default Unsubscribe;